import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bot, Clock, Trash2, ExternalLink, Filter, RefreshCw, Check, ChevronDown, ChevronLeft, ChevronRight, BookOpen, FileText, GraduationCap, Image, Video, Newspaper, Zap, User, Search, TrendingUp, Book, Presentation, BarChart3, FlaskConical, Feather } from 'lucide-react'
import { AgentHistoryService } from '../services/agentHistoryService'
import { useAuth } from '../hooks/useSupabaseAuth.jsx'
import { AGENT_TYPES } from '../constants/agentConfig.js'
import LoadingSkeleton from '../components/LoadingSkeleton'

// Custom Checkbox Component
const CustomCheckbox = ({ checked, onChange, className = '', disabled = false }) => {
  return (
    <div className={`relative ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
      />
      <div
        onClick={!disabled ? onChange : undefined}
        className={`
          w-4 h-4 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center
          ${checked 
            ? 'bg-brand-primary border-brand-primary' 
            : 'bg-transparent border-border-secondary hover:border-brand-primary/50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
        `}
      >
        {checked && (
          <Check className="w-2.5 h-2.5 text-white stroke-[3]" />
        )}
      </div>
    </div>
  )
}

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, className = '', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState('bottom')
  const buttonRef = React.useRef(null)
  const selectedOption = options.find(opt => opt.value === value)

  // Calculate dropdown position when opening
  const handleToggle = () => {
    if (disabled) return
    
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      const dropdownHeight = Math.min(options.length * 42 + 8, 300) // Estimate dropdown height
      
      // Use bottom position if there's enough space below, otherwise use top
      if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
        setDropdownPosition('bottom')
      } else {
        setDropdownPosition('top')
      }
    }
    
    setIsOpen(!isOpen)
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-3 py-2.5 bg-bg-tertiary border border-border-primary rounded-lg
          text-text-primary text-sm transition-all duration-200 flex items-center justify-between
          h-[42px] min-h-[42px]
          ${disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:border-brand-primary/50 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/20'
          }
        `}
      >
        <span>{selectedOption?.label || value}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className={`
            absolute left-0 right-0 bg-bg-secondary border border-border-primary rounded-lg shadow-xl z-20 overflow-hidden max-h-[300px] overflow-y-auto
            ${dropdownPosition === 'bottom' 
              ? 'top-full mt-1' 
              : 'bottom-full mb-1'
            }
          `}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange({ target: { value: option.value } })
                  setIsOpen(false)
                }}
                className={`
                  w-full px-3 py-2.5 text-left text-sm transition-all duration-150
                  ${value === option.value 
                    ? 'bg-brand-primary/20 text-brand-primary' 
                    : 'text-text-primary hover:bg-bg-tertiary'
                  }
                `}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Custom Search Input Component
const CustomSearchInput = ({ value, onChange, placeholder, className = '', disabled = false }) => {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`
          w-full px-3 py-2.5 bg-bg-tertiary border border-border-primary rounded-lg
          text-text-primary placeholder-text-quaternary text-sm transition-all duration-200
          ${disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'hover:border-brand-primary/50 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/20'
          }
        `}
      />
    </div>
  )
}

// Custom Pagination Component
const CustomPagination = ({ currentPage, totalPages, onPageChange, disabled = false }) => {
  if (totalPages <= 1) return null

  const getVisiblePages = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  const visiblePages = getVisiblePages()

  return (
    <div className="flex items-center justify-center gap-1 mt-6">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={disabled || currentPage === 1}
        className="p-2 rounded-lg border border-border-primary bg-bg-tertiary hover:bg-bg-quaternary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      
      {visiblePages.map((page, index) => (
        page === '...' ? (
          <span key={`dots-${index}`} className="px-3 py-2 text-text-quaternary">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            disabled={disabled}
            className={`
              px-3 py-2 rounded-lg border transition-all text-sm font-medium
              ${currentPage === page
                ? 'bg-brand-primary border-brand-primary text-white'
                : 'border-border-primary bg-bg-tertiary hover:bg-bg-quaternary text-text-primary disabled:opacity-50 disabled:cursor-not-allowed'
              }
            `}
          >
            {page}
          </button>
        )
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={disabled || currentPage === totalPages}
        className="p-2 rounded-lg border border-border-primary bg-bg-tertiary hover:bg-bg-quaternary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  )
}

const AgentHistoryPage = () => {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [histories, setHistories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all') // 'all', 'skripsi_agent', 'artikel_agent', 'makalah_agent', 'gambar_agent', 'berita_agent'
  const [searchTerm, setSearchTerm] = useState('')
  const [stats, setStats] = useState({ total: 0, skripsi_agent: 0, artikel_agent: 0, makalah_agent: 0, gambar_agent: 0, video_agent: 0, buku_agent: 0, novel_agent: 0, berita_agent: 0, trending_agent: 0, search_agent: 0, writing_agent: 0, ppt_agent: 0, chart_agent: 0, research_agent: 0, pdf_agent: 0 })
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null) // 'selected' or historyId
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [isCleaningDuplicates, setIsCleaningDuplicates] = useState(false)
  const itemsPerPage = 20

  // Load histories
  const loadHistories = async (page = currentPage) => {
    try {
      setLoading(true)
      setError(null)
      
      // Check if user is authenticated before making requests
      if (!user) {
        setError('Please log in to view your agent history.')
        setLoading(false)
        return
      }
      
      const offset = (page - 1) * itemsPerPage
      
      let result
      if (searchTerm.trim()) {
        result = await AgentHistoryService.searchInAgentHistory(searchTerm, { 
          limit: itemsPerPage,
          offset: offset,
          orderBy: 'last_accessed',
          orderDirection: 'desc'
        })
      } else {
        result = await AgentHistoryService.getAgentHistory({ 
          agentType: filter,
          limit: itemsPerPage,
          offset: offset,
          orderBy: 'last_accessed',
          orderDirection: 'desc'
        })
      }
      
      if (result.error) {
        // Handle specific authentication errors
        if (result.error.includes('not authenticated') || result.error.includes('session')) {
          setError('Your session has expired. Please log in again.')
        } else {
          setError(result.error)
        }
        return
      }
      
      setHistories(result.data || [])
      setTotalItems(result.total || 0)
      setTotalPages(Math.ceil((result.total || 0) / itemsPerPage))
      
      // Reset current page if it exceeds total pages
      if (page > Math.ceil((result.total || 0) / itemsPerPage) && result.total > 0) {
        setCurrentPage(1)
        loadHistories(1)
        return
      }
      
    } catch (err) {
      console.error('Error loading agent histories:', err)
      
      // Handle specific error types
      if (err.message.includes('not authenticated') || err.message.includes('session')) {
        setError('Your session has expired. Please log in again.')
      } else if (err.message.includes('No API key found')) {
        setError('Authentication error. Please refresh the page and try again.')
      } else {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }

  // Load stats
  const loadStats = async () => {
    try {
      const result = await AgentHistoryService.getAgentHistoryStats()
      if (result.data) {
        setStats(result.data)
      }
    } catch (err) {
      console.error('Error loading agent stats:', err)
    }
  }

  // Initial load and filter change
  useEffect(() => {
    if (user) {
      setCurrentPage(1)
      loadHistories(1)
      loadStats()
    }
  }, [user, filter])

  // Page change effect (only when not resetting to page 1)
  useEffect(() => {
    if (user && currentPage > 1) {
      loadHistories(currentPage)
    }
  }, [currentPage])

  // Search effect with debounce
  useEffect(() => {
    if (user) {
      const timeoutId = setTimeout(() => {
        if (currentPage === 1) {
          loadHistories(1)
        } else {
          setCurrentPage(1) // This will trigger the page change effect
        }
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [searchTerm])

  // Handle view result
  const handleViewResult = async (history) => {
    try {
      // Navigate to appropriate agent result page based on agent type
      switch (history.agent_type) {
        case AGENT_TYPES.SKRIPSI:
        case 'skripsi_agent': // Handle legacy database values
        navigate(`/agents/skripsi_agent/result/${history.id}`)
        break
      case AGENT_TYPES.ARTIKEL:
      case 'artikel_agent': // Handle legacy database values
        navigate(`/agents/artikel_agent/result/${history.id}`)
        break
      case AGENT_TYPES.MAKALAH:
      case 'makalah_agent': // Handle legacy database values
        navigate(`/agents/makalah_agent/result/${history.id}`)
        break
      case AGENT_TYPES.GAMBAR:
      case 'gambar_agent': // Handle legacy database values
        navigate(`/agents/gambar_agent/result/${history.id}`)
        break
      case AGENT_TYPES.VIDEO:
      case 'video_agent': // Handle legacy database values
        navigate(`/agents/video_agent/result/${history.id}`)
        break
      case AGENT_TYPES.BUKU:
      case 'buku_agent': // Handle legacy database values
        navigate(`/agents/buku_agent/result/${history.id}`)
        break
      case AGENT_TYPES.NOVEL:
      case 'novel_agent': // Handle legacy database values
        navigate(`/agents/novel_agent/result/${history.id}`)
        break
      case AGENT_TYPES.BERITA:
      case 'berita_agent': // Handle legacy database values
        navigate(`/agents/berita_agent/result/${history.id}`)
        break
      case AGENT_TYPES.SEARCH:
      case 'search_agent': // Handle legacy database values
        navigate(`/agents/search_agent/result/${history.id}`)
        break
      case AGENT_TYPES.TRENDING:
      case 'trending_agent': // Handle legacy database values
        navigate(`/agents/trending_agent/result/${history.id}`)
        break
      case AGENT_TYPES.WRITING:
      case 'writing_agent': // Handle legacy database values
        // For writing agent, navigate directly to editor with continue mode
        try {
          // Get the writing data from history
          const writingData = {
            title: history.content?.title || '',
            content: history.content?.content || '',
            query: history.query,
            timestamp: Date.now()
          }
          
          // Store in sessionStorage for continue editing
          sessionStorage.setItem('writingContinueData', JSON.stringify(writingData))
          
          // Navigate directly to writing agent with continue mode
          navigate(`/agent/writing_agent?continue=true&query=${encodeURIComponent(history.query)}`)
        } catch (error) {
          console.error('Error preparing writing data:', error)
          // Fallback to result page if there's an error
          navigate(`/agents/writing_agent/result/${history.id}`)
        }
        break
      case AGENT_TYPES.PPT:
      case 'ppt_agent': // Handle legacy database values
        navigate(`/agents/ppt_agent/result/${history.id}`)
        break
      case AGENT_TYPES.CHART:
      case 'chart_agent': // Handle legacy database values
        navigate(`/agents/chart_agent/result/${history.id}`)
        break
      case AGENT_TYPES.RESEARCH:
      case 'research_agent': // Handle legacy database values
        navigate(`/agents/research_agent/result/${history.id}`)
        break
      case AGENT_TYPES.PDF:
      case 'pdf_agent': // Handle legacy database values
        navigate(`/agents/pdf_agent/result/${history.id}`)
        break
        default:
          console.error('Unknown agent type:', history.agent_type)
      }
    } catch (err) {
      console.error('Error viewing agent result:', err)
    }
  }

  // Handle delete single item
  const handleDelete = async (historyId) => {
    setDeleteTarget(historyId)
    setShowDeleteConfirm(true)
  }

  // Handle delete selected items
  const handleDeleteSelected = () => {
    if (selectedItems.size > 0) {
      setDeleteTarget('selected')
      setShowDeleteConfirm(true)
    }
  }

  // Handle cleanup duplicates
  const handleCleanupDuplicates = async () => {
    try {
      setIsCleaningDuplicates(true)
      const result = await AgentHistoryService.cleanupDuplicates()
      
      if (result.error) {
        throw new Error(result.error)
      }
      
      if (result.deletedCount > 0) {
        // Reload data after cleanup
        await loadHistories(currentPage)
        await loadStats()
        
        // Show success message
        alert(`Berhasil membersihkan ${result.deletedCount} duplikasi dari riwayat agent.`)
      } else {
        alert('Tidak ada duplikasi yang ditemukan.')
      }
    } catch (err) {
      console.error('Error cleaning up duplicates:', err)
      alert('Terjadi kesalahan saat membersihkan duplikasi: ' + err.message)
    } finally {
      setIsCleaningDuplicates(false)
    }
  }

  // Confirm delete
  const confirmDelete = async () => {
    try {
      setLoading(true)
      
      if (deleteTarget === 'selected') {
        // Delete multiple items
        const deletePromises = Array.from(selectedItems).map(id => 
          AgentHistoryService.deleteAgentHistory(id)
        )
        await Promise.all(deletePromises)
        setSelectedItems(new Set())
      } else {
        // Delete single item
        await AgentHistoryService.deleteAgentHistory(deleteTarget)
      }
      
      // Reload data
      await loadHistories(currentPage)
      await loadStats()
      
    } catch (err) {
      console.error('Error deleting agent history:', err)
      setError(err.message)
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
      setDeleteTarget(null)
    }
  }

  // Handle select item
  const handleSelectItem = (historyId) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(historyId)) {
      newSelected.delete(historyId)
    } else {
      newSelected.add(historyId)
    }
    setSelectedItems(newSelected)
  }

  // Handle select all
  const handleSelectAll = () => {
    if (selectedItems.size === histories.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(histories.map(h => h.id)))
    }
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) {
      return 'Hari ini'
    } else if (diffDays === 1) {
      return 'Kemarin'
    } else if (diffDays < 7) {
      return `${diffDays} hari lalu`
    } else {
      return date.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    }
  }

  // Get agent icon
  const getAgentIcon = (agentType) => {
    switch (agentType) {
      case AGENT_TYPES.SKRIPSI:
      case 'skripsi_agent': // Handle legacy database values
        return BookOpen
      case AGENT_TYPES.ARTIKEL:
      case 'artikel_agent': // Handle legacy database values
        return FileText
      case AGENT_TYPES.MAKALAH:
      case 'makalah_agent': // Handle legacy database values
        return GraduationCap
      case AGENT_TYPES.GAMBAR:
      case 'gambar_agent': // Handle legacy database values
        return Image
      case AGENT_TYPES.VIDEO:
      case 'video_agent': // Handle legacy database values
        return Video
      case AGENT_TYPES.BUKU:
      case 'buku_agent': // Handle legacy database values
        return Book
      case AGENT_TYPES.NOVEL:
      case 'novel_agent': // Handle legacy database values
        return Feather
      case AGENT_TYPES.BERITA:
      case 'berita_agent': // Handle legacy database values
        return Newspaper
      case AGENT_TYPES.SEARCH:
      case 'search_agent': // Handle legacy database values
        return Search
      case AGENT_TYPES.TRENDING:
      case 'trending_agent': // Handle legacy database values
        return TrendingUp
      case AGENT_TYPES.WRITING:
      case 'writing_agent': // Handle legacy database values
        return FileText
      case AGENT_TYPES.PPT:
      case 'ppt_agent': // Handle legacy database values
        return Presentation
      case AGENT_TYPES.CHART:
       case 'chart_agent': // Handle legacy database values
         return BarChart3
      case AGENT_TYPES.RESEARCH:
      case 'research_agent': // Handle legacy database values
        return FlaskConical
      case AGENT_TYPES.PDF:
      case 'pdf_agent': // Handle legacy database values
        return FileText
      default:
        return User
    }
  }

  // Get agent color
  const getAgentColor = (agentType) => {
    switch (agentType) {
       case AGENT_TYPES.SKRIPSI:
       case 'skripsi_agent': // Handle legacy database values
         return 'text-emerald-400'
       case AGENT_TYPES.ARTIKEL:
       case 'artikel_agent': // Handle legacy database values
         return 'text-blue-400'
       case AGENT_TYPES.MAKALAH:
       case 'makalah_agent': // Handle legacy database values
         return 'text-violet-400'
       case AGENT_TYPES.GAMBAR:
       case 'gambar_agent': // Handle legacy database values
         return 'text-pink-400'
       case AGENT_TYPES.VIDEO:
       case 'video_agent': // Handle legacy database values
         return 'text-orange-500'
       case AGENT_TYPES.BUKU:
       case 'buku_agent': // Handle legacy database values
         return 'text-amber-400'
       case AGENT_TYPES.NOVEL:
       case 'novel_agent': // Handle legacy database values
         return 'text-indigo-400'
       case AGENT_TYPES.BERITA:
       case 'berita_agent': // Handle legacy database values
         return 'text-red-400'
       case AGENT_TYPES.SEARCH:
       case 'search_agent': // Handle legacy database values
         return 'text-cyan-400'
       case AGENT_TYPES.TRENDING:
       case 'trending_agent': // Handle legacy database values
         return 'text-orange-400'
       case AGENT_TYPES.WRITING:
       case 'writing_agent': // Handle legacy database values
         return 'text-purple-400'
       case AGENT_TYPES.PPT:
       case 'ppt_agent': // Handle legacy database values
         return 'text-indigo-400'
       case AGENT_TYPES.CHART:
       case 'chart_agent': // Handle legacy database values
         return 'text-emerald-600'
       case AGENT_TYPES.RESEARCH:
       case 'research_agent': // Handle legacy database values
         return 'text-red-600'
       case AGENT_TYPES.PDF:
       case 'pdf_agent': // Handle legacy database values
         return 'text-slate-400'
       default:
         return 'text-gray-400'
     }
  }

  // Get agent type label
  const getAgentTypeLabel = (agentType) => {
    switch (agentType) {
       case AGENT_TYPES.SKRIPSI:
       case 'skripsi_agent': // Handle legacy database values
         return 'Skripsi'
       case AGENT_TYPES.ARTIKEL:
       case 'artikel_agent': // Handle legacy database values
         return 'Artikel'
       case AGENT_TYPES.MAKALAH:
       case 'makalah_agent': // Handle legacy database values
         return 'Makalah'
       case AGENT_TYPES.GAMBAR:
       case 'gambar_agent': // Handle legacy database values
         return 'Gambar'
       case AGENT_TYPES.VIDEO:
       case 'video_agent': // Handle legacy database values
         return 'Video'
       case AGENT_TYPES.BUKU:
       case 'buku_agent': // Handle legacy database values
         return 'Buku'
       case AGENT_TYPES.NOVEL:
       case 'novel_agent': // Handle legacy database values
         return 'Novel'
       case AGENT_TYPES.BERITA:
       case 'berita_agent': // Handle legacy database values
         return 'Berita'
       case AGENT_TYPES.SEARCH:
       case 'search_agent': // Handle legacy database values
         return 'Search'
       case AGENT_TYPES.TRENDING:
       case 'trending_agent': // Handle legacy database values
         return 'Trending'
       case AGENT_TYPES.WRITING:
       case 'writing_agent': // Handle legacy database values
         return 'Writing'
       case AGENT_TYPES.PPT:
       case 'ppt_agent': // Handle legacy database values
         return 'PPT'
       case AGENT_TYPES.CHART:
       case 'chart_agent': // Handle legacy database values
         return 'Chart'
       case AGENT_TYPES.RESEARCH:
       case 'research_agent': // Handle legacy database values
         return 'Research'
       case AGENT_TYPES.PDF:
       case 'pdf_agent': // Handle legacy database values
         return 'PDF'
       default:
         return agentType || 'Unknown'
     }
  }

  // Get agent display name
  const getAgentDisplayName = (agentType) => {
    switch (agentType) {
      case 'skripsi_agent':
        return 'Skripsi'
      case 'artikel_agent':
        return 'Artikel'
      case 'makalah_agent':
        return 'Makalah'
      case 'gambar_agent':
        return 'Gambar'
      case 'video_agent':
        return 'Video'
      case 'buku_agent':
        return 'Buku'
      case 'novel_agent':
        return 'Novel'
      case 'berita_agent':
        return 'Berita'
      case 'search_agent':
        return 'Search'
      case 'trending_agent':
        return 'Trending'
      case 'writing_agent':
        return 'Writing'
      case 'ppt_agent':
        return 'PPT'
      case 'chart_agent':
        return 'Chart'
      case 'research_agent':
        return 'Research'
      case 'pdf_agent':
        return 'PDF'
      default:
        return agentType
    }
  }

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400'
      case 'failed':
        return 'text-red-400'
      case 'in_progress':
        return 'text-yellow-400'
      default:
        return 'text-gray-400'
    }
  }

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Selesai'
      case 'failed':
        return 'Gagal'
      case 'in_progress':
        return 'Proses'
      default:
        return status || 'Unknown'
    }
  }

  // Show loading state during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-text-tertiary">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center px-4">
        <div className="text-center max-w-md w-full">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <img 
              src="https://media.cara.id/images/logo-cara-white.png" 
              alt="Cara Logo" 
              className="h-16"
            />
          </div>
          
          {/* Main Message */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-6">
            <h2 className="text-2xl font-bold text-white mb-3">Akses Riwayat Agent</h2>
            <p className="text-gray-400 mb-6">
              Silakan login terlebih dahulu untuk melihat dan mengelola riwayat hasil agent AI Anda.
            </p>
            
            <button
              onClick={() => {
                // Dispatch custom event to open auth modal
                window.dispatchEvent(new CustomEvent('openAuthModal'))
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Login Sekarang
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
          
          {/* Additional Info */}
          <p className="text-sm text-gray-500">
            Belum punya akun? Login dengan Google untuk membuat akun baru.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-bg-primary">
      {/* Header */}
      <div className="border-b border-border-primary bg-bg-secondary/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex flex-col gap-4 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-text-primary mb-1 sm:mb-2">Riwayat Agent</h1>
                <p className="text-xs sm:text-sm text-text-tertiary">Kelola riwayat hasil agent AI Anda</p>
              </div>
              
              {/* Compact Stats - Three Row Layout */}
              <div className="space-y-2">
                {/* First Row - Total + 4 cards */}
                <div className="grid grid-cols-5 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 text-sm">
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-text-primary text-sm sm:text-base truncate">{stats.total}</div>
                    <div className="text-xs text-text-quaternary truncate">Total</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-emerald-400 text-sm sm:text-base truncate">{stats.skripsi_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Skripsi</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-blue-400 text-sm sm:text-base truncate">{stats.artikel_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Artikel</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-violet-400 text-sm sm:text-base truncate">{stats.makalah_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Makalah</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-pink-400 text-sm sm:text-base truncate">{stats.gambar_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Gambar</div>
                  </div>
                </div>
                
                {/* Second Row - 5 cards */}
                <div className="grid grid-cols-5 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 text-sm">
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-orange-500 text-sm sm:text-base truncate">{stats.video_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Video</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-red-400 text-sm sm:text-base truncate">{stats.berita_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Berita</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-cyan-400 text-sm sm:text-base truncate">{stats.search_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Search</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-orange-400 text-sm sm:text-base truncate">{stats.trending_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Trending</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-purple-400 text-sm sm:text-base truncate">{stats.writing_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Writing</div>
                  </div>
                </div>
                
                {/* Third Row - 5 cards */}
                <div className="grid grid-cols-5 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 text-sm">
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-indigo-400 text-sm sm:text-base truncate">{stats.ppt_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">PPT</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-amber-400 text-sm sm:text-base truncate">{stats.buku_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Buku</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-indigo-400 text-sm sm:text-base truncate">{stats.novel_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Novel</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-green-400 text-sm sm:text-base truncate">{stats.chart_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Chart</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-red-600 text-sm sm:text-base truncate">{stats.research_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">Research</div>
                  </div>
                  <div className="bg-bg-tertiary/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-center">
                    <div className="font-semibold text-slate-400 text-sm sm:text-base truncate">{stats.pdf_agent}</div>
                    <div className="text-xs text-text-quaternary truncate">PDF</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex flex-col gap-3">
            {/* Search */}
            <div className="w-full">
              <CustomSearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari dalam riwayat agent..."
                className="w-full h-[42px] min-h-[42px]"
                disabled={loading}
              />
            </div>
            
            {/* Filter & Actions */}
            <div className="flex gap-2 sm:gap-3">
              <div className="flex-1">
                <CustomDropdown
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  options={[
                     { value: 'all', label: 'Semua Agent' },
                     { value: AGENT_TYPES.SKRIPSI, label: 'Skripsi Agent' },
                     { value: AGENT_TYPES.ARTIKEL, label: 'Artikel Agent' },
                     { value: AGENT_TYPES.MAKALAH, label: 'Makalah Agent' },
                     { value: AGENT_TYPES.GAMBAR, label: 'Gambar Agent' },
                     { value: AGENT_TYPES.VIDEO, label: 'Video Agent' },
                     { value: AGENT_TYPES.BUKU, label: 'Buku Agent' },
                     { value: AGENT_TYPES.NOVEL, label: 'Novel Agent' },
                     { value: AGENT_TYPES.BERITA, label: 'Berita Agent' },
                     { value: AGENT_TYPES.SEARCH, label: 'Search Agent' },
                     { value: AGENT_TYPES.TRENDING, label: 'Trending Agent' },
                     { value: AGENT_TYPES.WRITING, label: 'Writing Agent' },
                     { value: AGENT_TYPES.PPT, label: 'PPT Agent' },
                     { value: AGENT_TYPES.CHART, label: 'Chart Agent' },
                     { value: AGENT_TYPES.RESEARCH, label: 'Research Agent' },
                     { value: AGENT_TYPES.PDF, label: 'PDF Agent' }
                   ]}
                  className="w-full h-[42px] min-h-[42px]"
                  disabled={loading}
                />
              </div>
              
              <button
                onClick={() => loadHistories(currentPage)}
                disabled={loading}
                className="px-3 py-2.5 bg-bg-tertiary hover:bg-bg-quaternary border border-border-primary rounded-lg text-text-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed h-[42px] min-h-[42px] flex items-center justify-center flex-shrink-0"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              
              <button
                onClick={handleCleanupDuplicates}
                disabled={loading || isCleaningDuplicates}
                className="px-3 py-2.5 bg-yellow-600 hover:bg-yellow-700 border border-yellow-500 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed h-[42px] min-h-[42px] flex items-center justify-center flex-shrink-0"
                title="Bersihkan duplikasi dalam riwayat agent"
              >
                <Filter className={`w-4 h-4 ${isCleaningDuplicates ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Bulk actions */}
          {selectedItems.size > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-bg-tertiary/60 border border-border-primary rounded-lg p-3">
              <span className="text-text-secondary text-sm">
                {selectedItems.size} item dipilih
              </span>
              <button
                onClick={handleDeleteSelected}
                className="px-3 py-1.5 bg-red-600/90 hover:bg-red-600 rounded-lg text-white transition-all flex items-center gap-2 text-sm w-full sm:w-auto justify-center sm:justify-start"
              >
                <Trash2 className="w-4 h-4" />
                Hapus
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          {loading && histories.length === 0 ? (
            <LoadingSkeleton />
          ) : error ? (
            <div className="text-center py-12">
              <div className="bg-bg-tertiary/50 rounded-lg p-4 sm:p-6 max-w-md mx-auto">
                <div className="text-red-400 mb-2 font-medium text-sm sm:text-base">Gagal memuat riwayat agent</div>
                <div className="text-text-tertiary text-xs sm:text-sm mb-4">{error}</div>
                <button
                  onClick={() => loadHistories(currentPage)}
                  className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary rounded-lg text-white transition-colors text-xs sm:text-sm"
                >
                  Coba Lagi
                </button>
              </div>
            </div>
          ) : histories.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="bg-bg-tertiary/30 rounded-lg p-6 sm:p-8 max-w-md mx-auto">
                <div className="text-text-tertiary mb-4 text-sm sm:text-base">
                  {searchTerm ? 'Tidak ada hasil ditemukan' : 'Belum ada riwayat agent'}
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 bg-brand-primary hover:bg-brand-secondary rounded-lg text-white transition-colors text-sm sm:text-base"
                >
                  Mulai Menggunakan Agent
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Select all */}
              {histories.length > 0 && (
                <div className="flex items-center gap-3 pb-3 mb-2 border-b border-border-primary">
                  <CustomCheckbox
                    checked={selectedItems.size === histories.length}
                    onChange={handleSelectAll}
                    disabled={loading}
                  />
                  <span className="text-text-quaternary text-xs sm:text-sm">Pilih semua ({histories.length} item)</span>
                </div>
              )}
              
              {/* History items */}
              {histories.map((history) => {
                const AgentIcon = getAgentIcon(history.agent_type)
                const agentColor = getAgentColor(history.agent_type)
                const statusColor = getStatusColor(history.status)
                
                return (
                  <div
                    key={history.id}
                    className="group bg-bg-tertiary/40 hover:bg-bg-tertiary/70 rounded-lg border border-border-primary hover:border-border-secondary transition-all duration-200"
                  >
                    <div className="p-3 sm:p-4">
                      <div className="flex items-start gap-3 sm:gap-4">
                        {/* Checkbox */}
                        <div className="flex-shrink-0 pt-1">
                          <CustomCheckbox
                            checked={selectedItems.has(history.id)}
                            onChange={() => handleSelectItem(history.id)}
                            disabled={loading}
                            className="mt-0.5"
                          />
                        </div>
                        
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-bg-quaternary/60 flex items-center justify-center">
                            <AgentIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${agentColor}`} />
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-text-primary mb-2 line-clamp-2 text-sm sm:text-base">
                            {history.agent_type === 'writing_agent' || history.agent_type === AGENT_TYPES.WRITING 
                              ? (history.content?.title || history.title || 'Untitled')
                              : (history.title || 'Untitled')
                            }
                          </h3>
                          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-text-tertiary mb-3">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              {formatDate(history.last_accessed)}
                            </span>
                            <span className="flex items-center gap-1">
                              <AgentIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                              {getAgentTypeLabel(history.agent_type)}
                            </span>
                            <span className={`flex items-center gap-1 ${statusColor}`}>
                              <div className="w-2 h-2 rounded-full bg-current" />
                              {getStatusLabel(history.status)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                          {history.status === 'completed' && (
                            <button
                              onClick={() => handleViewResult(history)}
                              className="px-2 sm:px-3 py-1.5 bg-brand-primary hover:bg-brand-secondary text-white rounded-lg transition-colors text-xs sm:text-sm font-medium min-w-0 flex items-center justify-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">Lihat</span>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(history.id)}
                            className="px-2 sm:px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg transition-colors text-xs sm:text-sm flex items-center justify-center"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
              
              {/* Pagination */}
              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                disabled={loading}
              />
            </div>
          )}
          
          {/* Results info */}
          {histories.length > 0 && (
            <div className="mt-4 text-center text-xs sm:text-sm text-text-quaternary">
              Menampilkan {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} dari {totalItems} item
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-bg-secondary border border-border-primary rounded-lg p-4 sm:p-6 max-w-md w-full mx-3 sm:mx-4 shadow-2xl">
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4">
              Konfirmasi Hapus
            </h3>
            <p className="text-sm sm:text-base text-text-secondary mb-4 sm:mb-6">
              {deleteTarget === 'selected' 
                ? `Apakah Anda yakin ingin menghapus ${selectedItems.size} item yang dipilih?`
                : 'Apakah Anda yakin ingin menghapus item ini?'
              }
              <br />
              <span className="text-xs sm:text-sm text-text-quaternary">Tindakan ini tidak dapat dibatalkan.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteTarget(null)
                }}
                className="px-4 py-2 bg-bg-tertiary hover:bg-bg-quaternary border border-border-primary rounded-lg text-text-secondary transition-all text-sm sm:text-base order-2 sm:order-1"
              >
                Batal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-1 sm:order-2"
              >
                {loading ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgentHistoryPage